// React
import React from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
// Locales
import translate from 'locales/i18n';
// Material components
import { withStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
// PCP Components
import DialogManageUser from 'components/dialogs/user/DialogManageUser';
import OnMousePopover from 'components/commons/OnMousePopover';
// Stores
import LoggedUserStore from 'stores/LoggedUserStore';
import UserStore from 'stores/UserStore';
import AccountStore from 'stores/AccountStore';
// Action
import UserAction from 'actions/UserAction';
import AccountAction from 'actions/AccountAction';
// Theme
import theme from 'assets/theme/theme';
import styles from 'components/page/UserManagerPage/UserManagerPage.style';

class UsersManagerPage extends React.PureComponent {

    state = {
        users: UserStore.getUsers(),
        searchText: '',
        isLoading: UserStore.isLoading(),
        dialogManageUserOpen: false,
        currentUser: null
    };

    static propTypes = {
        classes: PropTypes.object
    };

    componentDidMount() {
        UserStore.addChangeListener(this.onUserChange);

        if (UserStore.getUsers().length === 0 && LoggedUserStore.isLogged())
            UserAction.getUsers();

        this.mounted = true;
    }

    componentWillUnmount() {
        UserStore.removeChangeListener(this.onUserChange);

        this.mounted = false;
    }

    onUserChange = () => {
        if (!this.mounted) {
            return false;
        }

        this.setState({
            users: UserStore.getUsers(),
            isLoading: UserStore.isLoading()
        });
    };

    updateUser = id => event => {
        const user = this.state.users.find(s => s.id === id);
        UserAction.updateUser({ [event.target.value]: !user[event.target.value] }, id);
    };

    deleteUser = id => {
        UserAction.deleteUser(id);
    };

    close = () => {
        this.setState({ dialogManageUserOpen: false, currentUser: null });
    };

    renderDialogManageUser = () =>
        <DialogManageUser
            open={this.state.dialogManageUserOpen}
            close={this.close}
            user={this.state.currentUser}
            key={this.state.currentUser ? this.state.currentUser.id : null}
        />;

    render() {
        const { classes } = this.props;

        if (!LoggedUserStore.isLogged()) {
            return (
                <Redirect to='/login' />
            );
        }

        const users = this.state.users.filter(user => (
            user.login + user.firstName + user.lastName).toLowerCase().includes(this.state.searchText.toLowerCase())
            || user.accounts.find(a => a.name.toLowerCase().includes(this.state.searchText.toLowerCase()))
        );

        return (
            <div className={classes.root}>
                <div className={classes.header}>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={() => {
                            this.setState({ dialogManageUserOpen: true });
                            if (AccountStore.getAccounts().length === 0 && LoggedUserStore.isLogged())
                                AccountAction.getAccounts();
                        }}>
                        <AddIcon className={classes.leftIcon} />
                        User
                    </Button>
                    <TextField
                        id='searchfield'
                        className={classes.searchBar}
                        variant='outlined'
                        size='small'
                        value={this.state.searchText}
                        onChange={event => this.setState({ searchText: event.target.value })}
                        onKeyPress={this.handleKeyPress}
                        placeholder={'User\'s name'}
                        margin={'dense'}
                        InputProps={{
                            startAdornment: <InputAdornment position='start'><SearchIcon className={classes.searchIcon} /></InputAdornment>
                        }}
                    />
                </div>
                <Paper className={classes.paper}>
                    {this.state.isLoading ?
                        <div className={classes.loaderContainer}><CircularProgress /></div>
                        :
                        <Table className={classes.table} stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell align='left' className={classes.tableHeadCell} style={{ paddingLeft: '16px' }}>Login</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>{translate.i18n('FIRSTNAME')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>{translate.i18n('LASTNAME')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>{translate.i18n('ACCOUNTS')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>{translate.i18n('MAX_CONNECTION')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>{translate.i18n('LANGUAGE')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>{translate.i18n('PAGE_SIZE')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user, key) => <TableRow key={key} style={{ backgroundColor: key % 2 === 0 ? theme.palette.grey.semilight : theme.palette.white.main }}>
                                    <TableCell align='left' className={classes.basicCell} style={{ paddingLeft: '16px' }}>
                                        <div className={classes.firstCellContent}>{user.login}
                                            {user.isBlocked ?
                                                <svg width='16px' height='16px' viewBox='0 0 470 470' style={{ marginLeft: '4px', marginBottom: '4px' }} fill={theme.palette.red.main}>
                                                    <path d='M352,149.333h-21.333v-42.667C330.667,47.787,282.88,0,224,0S117.333,47.787,117.333,106.667v42.667H96c-23.573,0-42.667,19.093-42.667,42.667v213.333C53.333,428.907,72.427,448,96,448h256c23.573,0,42.667-19.093,42.667-42.667V192C394.667,168.427,375.573,149.333,352,149.333z M224,341.333c-23.573,0-42.667-19.093-42.667-42.667C181.333,275.093,200.427,256,224,256s42.667,19.093,42.667,42.667C266.667,322.24,247.573,341.333,224,341.333z M290.133,149.333H157.867v-42.667c0-36.48,29.653-66.133,66.133-66.133s66.133,29.653,66.133,66.133V149.333z' />
                                                </svg>
                                                :
                                                null
                                            }
                                        </div>
                                    </TableCell>
                                    <TableCell align='center' className={classes.basicCell}>{user.firstName ? user.firstName : '-'}</TableCell>
                                    <TableCell align='center' className={classes.basicCell}>{user.lastName ? user.lastName : '-'}</TableCell>
                                    <TableCell align='left'>{user.accounts ? user.accounts.length < 2 ? user.accounts[0] ? user.accounts[0].name : '-' : <OnMousePopover text={translate.i18n('MULTIPLE_ACCOUNTS')}>{user.accounts.map(a => <Typography key={a.name}>{a.name}</Typography>)}</OnMousePopover> : '-'}</TableCell>
                                    <TableCell align='center' className={classes.basicCell}>{user.maxConnection}</TableCell>
                                    <TableCell align='center' className={classes.basicCell}>{user.settings.language}</TableCell>
                                    <TableCell align='center' className={classes.basicCell}>{user.settings.pageSize}</TableCell>
                                    <TableCell align='center' className={classes.basicCell} style={{ width: '60px' }}>
                                        <IconButton aria-label='delete' onClick={() => {
                                            this.setState({ currentUser: user, dialogManageUserOpen: true });
                                            if (AccountStore.getAccounts().length === 0 && LoggedUserStore.isLogged())
                                                AccountAction.getAccounts();
                                        }}>
                                            <EditIcon fontSize='small' />
                                        </IconButton></TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    }
                </Paper>
                {this.renderDialogManageUser()}
            </div>
        );
    }
}

export default withStyles(styles)(UsersManagerPage);
